/**
 * Return an array of objects that contain the total quantity of each product in the cart.
 *
 * @param {CartLine[]} cartLines
 *
 * @return {{branchID: number, productID: number, quantity: number, qtyBeforeAdjustment: number, requiredQuantity: number}[]}
 */
const cartProductQuantityCalculator = (cartLines) => {
    let productQuantities = {};

    cartLines.forEach((line) => {
        // We will use a combination of branchID and itemID to calculate quantities, because stock
        // is checked separately for different branches.
        const key = `${line.branch.ID}-${line.item.ID}`;

        // Create a line for this product if one doesn't already exist.
        if (!productQuantities.hasOwnProperty(key)) {
            productQuantities[key] = {
                branchID: line.branch.ID,
                productID: line.item.ID,
                quantity: 0,
                qtyBeforeAdjustment: 0,
                requiredQuantity: 0,
            };
        }

        // How much of an item do we want in total on this line?
        let quantity = line.quantity;

        // 0 or the quantity already purchased if this an order edit.
        let qtyBeforeAdjustment = line.qtyBeforeAdjustment;

        // How much (additional) product is required for this line?
        // For example if this line contains 3 hats that have already been purchased and we edit the order and change
        // this to 6 hats we only need to check that 3 hats are in stock (not 6) because we already have 3.
        let requiredQuantity = quantity - qtyBeforeAdjustment;

        // In a normal order rather than an order edit we can have -ve quantities that represent returns.
        // If an item is being returned (-ve qty) it may be possible to count it as available stock if returnToStock is
        // set. Otherwise just set the quantity and requiredQuantity to 0.
        // We know this cart is a normal order not an edit because qtyBeforeAdjustment === 0
        if (qtyBeforeAdjustment === 0 && quantity < 0 && line.returnReason && !line.returnReason.returnToStock) {
            quantity = 0;
            requiredQuantity = 0;
        }

        // In an order edit cart we can't have -ve quantities (you can't do a return for an item that isn't already in
        // the cart), but you can reduce the quantity.

        // For example imagine an order with 2 burgers and 2 burgers with cheese (which are the same product but appear
        // on different lines because of options).
        //
        // You then edit this order to change to 1 burger and 3 cheese burgers.
        // If returnToStock === true
        // The burger line now has:             quantity +1, qtyBeforeAdjustment +2, requiredQuantity -1
        // The burger with cheese line now has: quantity +3, qtyBeforeAdjustment +2, requiredQuantity +1
        // The combined burger line has:        quantity +4, qtyBeforeAdjustment +4, requiredQuantity +0
        // But...
        // If returnToStock === false
        // The burger line now has:             quantity +1, qtyBeforeAdjustment +2, requiredQuantity +0
        // The burger with cheese line now has: quantity +3, qtyBeforeAdjustment +2, requiredQuantity +1
        // The combined burger line has:        quantity +4, qtyBeforeAdjustment +4, requiredQuantity +1
        // We know this cartLine is part of an order edit because qtyBeforeAdjustment !== 0
        if (qtyBeforeAdjustment > 0 && requiredQuantity < 0 && line.returnReason && !line.returnReason.returnToStock) {
            requiredQuantity = 0;
        }

        productQuantities[key].quantity += quantity;
        productQuantities[key].qtyBeforeAdjustment += qtyBeforeAdjustment;
        productQuantities[key].requiredQuantity += requiredQuantity;
    });

    // The overall requiredQuantity can never be -ve although individual lines can be.
    return Object.values(productQuantities).map((p) => {
        p.requiredQuantity = p.requiredQuantity < 0 ? 0 : p.requiredQuantity;
        return p;
    });
};

module.exports = cartProductQuantityCalculator;
